import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BrandingProvider } from "@/context/BrandingContext";
import CartDrawer from "@/components/CartDrawer";
import ScrollManager from "@/components/ScrollManager";
import InstallPWA from "@/components/InstallPWA";
import ShellLayout from "@/components/layout/ShellLayout";
import { ProtectedRoute, AdminRoute, AuthedRedirect } from "@/components/RouteGuards";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetail from "@/pages/ProductDetail";
import CourseDetail from "@/pages/CourseDetail";
import Certificate from "@/pages/Certificate";
import VerifyCertificate from "@/pages/VerifyCertificate";
import LegalPage from "@/pages/LegalPage";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import UserDashboard from "@/pages/UserDashboard";
import Signals from "@/pages/Signals";
import Brokers from "@/pages/Brokers";
import Bundles from "@/pages/Bundles";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminBundles from "@/pages/admin/AdminBundles";
import AdminLicenses from "@/pages/admin/AdminLicenses";
import AdminSignals from "@/pages/admin/AdminSignals";
import AdminInsights from "@/pages/admin/AdminInsights";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminBlocklist from "@/pages/admin/AdminBlocklist";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminBrokers from "@/pages/admin/AdminBrokers";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminLaunch from "@/pages/admin/AdminLaunch";
import AdminApiKeys from "@/pages/admin/AdminApiKeys";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminPages from "@/pages/admin/AdminPages";
import AdminSupport from "@/pages/admin/AdminSupport";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BrandingProvider>
        <CartProvider>
        <ScrollManager />
        <InstallPWA />
        <CartDrawer />
        <Toaster
          position="top-right"
          theme="light"
          toastOptions={{
            style: {
              background: "#ffffff",
              border: "1px solid rgba(10,42,25,0.16)",
              color: "#0a2a19",
              fontFamily: "'Outfit', sans-serif",
            },
          }}
        />
        <Routes>
          <Route element={<ShellLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<AuthedRedirect><Login /></AuthedRedirect>} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/brokers" element={<Brokers />} />
            <Route path="/markets" element={<Navigate to="/dashboard" replace />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/bundles" element={<Bundles />} />
            <Route path="/store/:slug" element={<CategoryPage />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/course/:slug" element={<CourseDetail />} />
            <Route path="/certificate/:code" element={<Certificate />} />
            <Route path="/verify" element={<VerifyCertificate />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route
              path="/affiliate"
              element={
                <ProtectedRoute>
                  <AffiliateDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/pay/:orderId"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute>
                  <OrderSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="bundles" element={<AdminBundles />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="licenses" element={<AdminLicenses />} />
            <Route path="signals" element={<AdminSignals />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="insights" element={<AdminInsights />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="brokers" element={<AdminBrokers />} />
            <Route path="affiliates" element={<AdminAffiliates />} />
            <Route path="blocklist" element={<AdminBlocklist />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="launch" element={<AdminLaunch />} />
            <Route path="branding" element={<AdminBranding />} />
            <Route path="legal" element={<AdminLegal />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </CartProvider>
        </BrandingProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
